import './styles/main.css';

import { Router } from './lib/router.ts';
import { DresbyScene } from './three/scene.ts';
import { DresbyTerminal } from './components/Terminal.ts';
import { i18n } from './lib/i18n.ts';

import { renderHome,     initHome     } from './pages/Home.ts';
import { renderAbout                  } from './pages/About.ts';
import { renderApps,     initApps     } from './pages/Apps.ts';
import { renderProjects, initProjects } from './pages/Projects.ts';
import { renderStore,    initStore    } from './pages/Store.ts';
import { renderBlog,     initBlog     } from './pages/Blog.ts';
import { renderContact,  initContact  } from './pages/Contact.ts';

// ── Loading progress ────────────────────────────────────────────────────────
const loadingBar    = document.getElementById('loading-bar') as HTMLElement;
const loadingStatus = document.getElementById('loading-status') as HTMLElement;
const loadingScreen = document.getElementById('loading-screen') as HTMLElement;

function setProgress(pct: number, status: string): void {
  if (loadingBar)    loadingBar.style.width = `${pct}%`;
  if (loadingStatus) loadingStatus.textContent = status;
}

// ── WebGL check ─────────────────────────────────────────────────────────────
function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch { return false; }
}

// ── Portal label overlay ─────────────────────────────────────────────────────
let portalLabelUpdateId = 0;
function startPortalLabels(scene: DresbyScene): void {
  const container = document.getElementById('portal-labels')!;

  function updateLabels(): void {
    const positions = scene.getPortalScreenPositions(window.innerWidth, window.innerHeight);
    positions.forEach(p => {
      let el = container.querySelector(`[data-portal="${p.id}"]`) as HTMLElement | null;
      if (!el) {
        el = document.createElement('a');
        el.className = 'portal-label';
        el.setAttribute('data-portal', p.id);
        (el as HTMLAnchorElement).href = `#${p.id === 'projects' ? 'projects' : p.id}`;
        el.style.color = p.color;
        el.style.borderColor = `${p.color}30`;
        el.style.textShadow = `0 0 8px ${p.color}`;
        container.appendChild(el);
      }
      el.textContent = p.label;
      el.style.left  = `${p.x}px`;
      el.style.top   = `${p.y}px`;
      el.style.opacity = p.visible ? '1' : '0';
    });
    portalLabelUpdateId = requestAnimationFrame(updateLabels);
  }

  updateLabels();
}

function stopPortalLabels(): void {
  cancelAnimationFrame(portalLabelUpdateId);
  const container = document.getElementById('portal-labels');
  if (container) container.innerHTML = '';
}

// ── Page routing ──────────────────────────────────────────────────────────────
const router = new Router();
let sceneRef: DresbyScene | null = null;
let labelsActive = false;

const PAGE_RENDERERS: Record<string, { render: () => string; init?: () => void }> = {
  home:     { render: renderHome,     init: initHome     },
  about:    { render: renderAbout                        },
  apps:     { render: renderApps,     init: initApps     },
  projects: { render: renderProjects, init: initProjects },
  store:    { render: renderStore,    init: initStore    },
  blog:     { render: renderBlog,     init: initBlog     },
  contact:  { render: renderContact,  init: initContact  },
};

function activatePage(name: string): void {
  const allPages = document.querySelectorAll('.page');
  const overlay  = document.getElementById('scene-overlay');

  allPages.forEach(el => el.classList.remove('active'));
  document.querySelectorAll('.nav-link').forEach(el => {
    el.classList.toggle('active', el.getAttribute('data-page') === name);
  });

  const pageEl = document.getElementById(`page-${name}`);
  if (!pageEl) return;

  const renderer = PAGE_RENDERERS[name] ?? PAGE_RENDERERS['home'];

  // Render once (idempotent check via data-rendered attribute)
  if (!pageEl.dataset.rendered) {
    pageEl.innerHTML = renderer.render();
    pageEl.dataset.rendered = '1';
    renderer.init?.();
  }

  pageEl.classList.add('active');

  // Dim scene on non-home pages; show labels only on home
  const isHome = name === 'home';
  overlay?.classList.toggle('dimmed', !isHome);

  if (isHome && sceneRef && !labelsActive) {
    startPortalLabels(sceneRef);
    labelsActive = true;
  } else if (!isHome && labelsActive) {
    stopPortalLabels();
    labelsActive = false;
  }

  if (isHome && sceneRef) {
    sceneRef.resetCamera();
  }
}

// ── Mode switching ────────────────────────────────────────────────────────────
function initModeSwitcher(): void {
  document.querySelectorAll('.mode-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.mode-tab').forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      const mode = (tab as HTMLElement).dataset.mode;
      document.body.dataset.mode = mode ?? 'browse';

      if (mode === 'play' && router.currentRoute() !== 'home') {
        router.navigate('home');
      }
      if (mode === 'build') {
        router.navigate('projects');
      }
    });
  });
}

// ── Mobile menu ───────────────────────────────────────────────────────────────
function initMobileMenu(): void {
  const btn   = document.getElementById('mobile-menu-btn');
  const links = document.getElementById('nav-links');
  if (!btn || !links) return;

  btn.addEventListener('click', () => {
    const open = links.classList.toggle('open');
    btn.setAttribute('aria-expanded', String(open));
  });

  links.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      links.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });
}

// ── Language toggle ──────────────────────────────────────────────────────────
function initLanguageToggle(): void {
  const btn = document.getElementById('lang-toggle');
  if (!btn) return;

  btn.addEventListener('click', () => {
    i18n.toggle();
    // Re-render current page with new language
    const current = router.currentRoute() || 'home';
    const pageEl = document.getElementById(`page-${current}`);
    if (pageEl) {
      pageEl.dataset.rendered = '';
      const renderer = PAGE_RENDERERS[current] ?? PAGE_RENDERERS['home'];
      pageEl.innerHTML = renderer.render();
      pageEl.dataset.rendered = '1';
      renderer.init?.();
    }
  });

  // Initial DOM update
  i18n.updateDOM();
}

// ── Boot sequence ─────────────────────────────────────────────────────────────
async function boot(): Promise<void> {
  setProgress(10, i18n.t('loading.check'));

  const webglOk = hasWebGL();

  setProgress(30, i18n.t('loading.world'));

  if (webglOk) {
    const canvas = document.getElementById('dresby-canvas') as HTMLCanvasElement;
    try {
      sceneRef = new DresbyScene(canvas);
      sceneRef.setNavigationHandler((page) => {
        activatePage(page);
      });
      setProgress(60, i18n.t('loading.build'));
    } catch (e) {
      console.warn('Three.js scene failed:', e);
    }
  } else {
    document.getElementById('dresby-canvas')?.remove();
    const fallback = document.getElementById('webgl-fallback');
    fallback?.classList.remove('hidden');
  }

  setProgress(80, i18n.t('loading.routes'));

  // Set up router
  router
    .on('home',     () => activatePage('home'))
    .on('about',    () => activatePage('about'))
    .on('apps',     () => activatePage('apps'))
    .on('projects', () => activatePage('projects'))
    .on('store',    () => activatePage('store'))
    .on('blog',     () => activatePage('blog'))
    .on('contact',  () => activatePage('contact'))
    .on('*',        () => activatePage('home'));

  initModeSwitcher();
  initMobileMenu();
  initLanguageToggle();

  // Terminal
  const terminal = new DresbyTerminal((page) => {
    router.navigate(page);
    terminal.close();
  });

  setProgress(95, i18n.t('loading.ready'));

  // Small delay so the loading bar reaches 100% visually
  await new Promise(r => setTimeout(r, 400));
  setProgress(100, i18n.t('loading.welcome'));

  await new Promise(r => setTimeout(r, 500));

  // Hide loading screen
  loadingScreen.classList.add('done');
  setTimeout(() => loadingScreen.remove(), 700);

  // Start router (triggers initial page render)
  router.init();

  // Start portal labels if on home
  if (sceneRef && router.currentRoute() === 'home') {
    startPortalLabels(sceneRef);
    labelsActive = true;
  }
}

boot().catch(console.error);
